import React, { useState } from 'react';
import { Avatar, Box, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePresenceState } from '../../context/PresenceContext';
import { profilePictureUrl, memberInitials } from '../../utils/profilePicture';
import ProfilePicturePreview from '../ProfilePicturePreview';

// At most this many circles in the header, counting YOUR avatar: from the
// (MAX_PEOPLE + 1)th person on the page, the leftmost slot becomes a "+N" circle.
const MAX_PEOPLE = 5;
const OTHER_SLOTS = MAX_PEOPLE - 1; // one circle is always you

const displayName = (member) => (
  member.username || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email
);

/**
 * Avatars of the other people on this exact page, Notion-style. You are not
 * in the stack: Header's right side already shows your own picture.
 *
 * Rendered in Header's right-hand group, just left of your own avatar, and
 * stacks towards the left. The data comes from PresenceProvider (App.js), whose
 * one socket per tab outlives route changes. Renders nothing when you are alone.
 */
const PagePresence = () => {
  const theme = useTheme();
  // From the app-wide PresenceProvider (one socket per tab, shared with tables).
  const { others } = usePresenceState();
  // A face clicked to see who it is: { url, alt } or null.
  const [picturePreview, setPicturePreview] = useState(null);
  // The "+N" circle, when clicked: anchors the list of people it hides.
  const [moreAnchor, setMoreAnchor] = useState(null);

  const preview = (
    <ProfilePicturePreview picture={picturePreview} onClose={() => setPicturePreview(null)} />
  );
  // Keep the preview mounted even if that person leaves while it is open.
  if (others.length === 0) return preview;

  const overflow = others.length > OTHER_SLOTS;
  const visible = overflow ? others.slice(0, OTHER_SLOTS - 1) : others;
  const hidden = overflow ? others.slice(OTHER_SLOTS - 1) : [];

  // How far each circle slides under its right-hand neighbour (theme spacing 1).
  const OVERLAP_PX = 8;

  const avatarSx = {
    width: 28,
    height: 28,
    fontSize: 12,
    border: `2px solid ${theme.palette.background.default}`,
  };

  // row-reverse: the first arrival sits nearest your own avatar on the right,
  // later arrivals and then the "+N" circle pile up towards the LEFT. Every
  // item overlaps the one to its right -- the nearest one tucks in behind your
  // own header avatar -- and nearer items are drawn on top.
  const moreLabel = `+${hidden.length}`;
  const items = [
    ...visible.map((member) => {
      const pictureUrl = profilePictureUrl(member.email, member.profile_picture_version);
      return (
        <Tooltip key={member.email} title={`${displayName(member)} is on this page`}>
          <Avatar
            src={pictureUrl || undefined}
            alt={displayName(member)}
            // A real picture opens enlarged, to tell people apart by face;
            // initials have nothing to enlarge.
            onClick={pictureUrl ? () => setPicturePreview({ url: pictureUrl, alt: displayName(member) }) : undefined}
            aria-label={pictureUrl ? `View ${displayName(member)}'s profile picture` : undefined}
            sx={{ ...avatarSx, cursor: pictureUrl ? 'pointer' : 'default' }}
          >
            {memberInitials(member.first_name, member.last_name)}
          </Avatar>
        </Tooltip>
      );
    }),
    ...(overflow ? [
      <Tooltip key="__more" title={hidden.map(displayName).join(', ')}>
        <Avatar
          // Click to list the hidden people -- the only way to reach their pictures.
          onClick={(event) => setMoreAnchor(event.currentTarget)}
          aria-haspopup="menu"
          sx={{
            cursor: 'pointer',
            ...avatarSx,
            // Smaller than the initials, and smaller again as the number grows,
            // so "+12" / "+123" still fit inside the 28px circle.
            fontSize: moreLabel.length <= 2 ? 11 : moreLabel.length === 3 ? 10 : 8.5,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            // The face to the right covers this circle's right OVERLAP_PX, so
            // centre the number in the part that stays visible.
            boxSizing: 'border-box',
            pr: `${OVERLAP_PX}px`,
          }}
        >
          {moreLabel}
        </Avatar>
      </Tooltip>,
    ] : []),
  ];

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'center' }}>
        {items.map((item, index) => (
          <Box
            key={item.key}
            sx={{ display: 'flex', position: 'relative', zIndex: items.length - index, mr: `-${OVERLAP_PX}px` }}
          >
            {item}
          </Box>
        ))}
      </Box>
      <Menu
        anchorEl={moreAnchor}
        open={Boolean(moreAnchor) && hidden.length > 0}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ dense: true, 'aria-label': 'Also on this page' }}
      >
        {hidden.map((member) => {
          const pictureUrl = profilePictureUrl(member.email, member.profile_picture_version);
          return (
            <MenuItem
              key={member.email}
              // Without a picture there is nothing to enlarge: listed, but greyed out.
              disabled={!pictureUrl}
              onClick={() => {
                setMoreAnchor(null);
                setPicturePreview({ url: pictureUrl, alt: displayName(member) });
              }}
              sx={{ gap: 1.5 }}
            >
              <Avatar src={pictureUrl || undefined} alt="" sx={{ width: 24, height: 24, fontSize: 10 }}>
                {memberInitials(member.first_name, member.last_name)}
              </Avatar>
              <ListItemText primary={displayName(member)} />
            </MenuItem>
          );
        })}
      </Menu>
      {preview}
    </>
  );
};

export default PagePresence;
